// Shared browser/Edge presentation labels. Never use these as Gemini synthesis identities.
export const UNSPECIFIED = "Not specified";
export const LANGUAGE_LABELS: Record<string, string> = {
  "ar-001": "Arabic – Modern Standard", "ar-EG": "Arabic – Egypt", "cs-CZ": "Czech",
  "de-DE": "German", "el-GR": "Greek", "en-AU": "English – Australia",
  "en-CA": "English – Canada", "en-GB": "English – United Kingdom", "en-IE": "English – Ireland",
  "en-IN": "English – India", "en-NZ": "English – New Zealand", "en-US": "English – United States",
  "en-ZA": "English – South Africa", "es-419": "Spanish – Latin America", "es-ES": "Spanish – Spain",
  "fr-CA": "French – Canada", "fr-FR": "French – France", "hi-IN": "Hindi – India",
  "it-IT": "Italian", "ja-JP": "Japanese", "jv-ID": "Javanese – Indonesia", "ko-KR": "Korean",
  "nl-NL": "Dutch", "pl-PL": "Polish", "pt-BR": "Portuguese – Brazil", "pt-PT": "Portuguese – Portugal",
  "sw-KE": "Swahili – Kenya", "ta-IN": "Tamil – India", "te-IN": "Telugu – India", "vi-VN": "Vietnamese",
};
const ACCENTS: Record<string, string> = {
  "General American": "American – General", "East Coast": "American – East Coast",
  "Gulf Coast": "Southern", "Inland Southern": "Southern", "Southern (Inland)": "Southern",
  "Midwest": "American – Midwest", "Northwest": "American – Northwest", "West Coast": "American – West Coast",
  "Modern Standard Arabic": "Arabic – Modern Standard", "Egyptian Arabic": "Egyptian – General",
  "Sydney English": "Australian – Sydney", "Toronto English": "Canadian – Toronto",
  "Vancouver English": "Canadian – Vancouver", "Montreal French": "Canadian – Montreal",
  "Bristol English": "British – Bristol", "Glasgow English": "British – Glasgow",
  "Liverpool English": "British – Liverpool", "Manchester English": "British – Manchester",
  "Newcastle English": "British – Newcastle", "Winchester English": "British – Winchester",
  "Dublin English": "Irish – Dublin", "Indian English": "Indian – General",
  "Auckland New Zealand English": "New Zealand – Auckland", "Cape Town English": "South African – Cape Town",
  "Argentina Spanish": "Argentinian – General", "Mexico Spanish": "Mexican – General",
  "Castilian Spanish": "Spanish – Castilian", "Paris French": "French – Paris",
  "Awadhi Hindi": "Indian – Awadhi", "Bhojpuri Hindi": "Indian – Bhojpuri",
  "Bundeli Hindi": "Indian – Bundeli", "Haryanvi Hindi": "Indian – Haryanvi", "Urdu": "Indian – Urdu",
  "Fukuoka Japanese": "Japanese – Fukuoka", "Osaka Japanese": "Japanese – Osaka", "Tokyo Japanese": "Japanese – Tokyo",
  "Busan Korean": "Korean – Busan", "Seoul Korean": "Korean – Seoul", "Javanese": "Indonesian – Java",
  "Brazilian Portuguese": "Brazilian – General", "Peninsular Portuguese": "Portuguese – Peninsular",
  "Swahili": "Kenyan – Swahili", "Tamil": "Indian – Tamil", "Telugu": "Indian – Telugu",
  "Czech": "Czech – General", "German": "German – General", "Standard German": "German – General",
  "Greek": "Greek – General", "Italian": "Italian – General", "Dutch": "Dutch – General",
  "Polish": "Polish – General", "Vietnamese": "Vietnamese – General",
};
export const record = (value: unknown): Record<string, unknown> =>
  value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : {};
export const text = (value: unknown) => typeof value === "string" ? value.trim() : "";
export function effectiveCatalog(metadata: unknown) {
  const meta = record(metadata);
  const source = Object.keys(record(meta.catalog)).length ? record(meta.catalog) : meta;
  return { ...source, ...record(meta.catalog_overrides) };
}
export function languageLabel(value: unknown): string {
  const code = text(value);
  if (!code) return UNSPECIFIED;
  if (LANGUAGE_LABELS[code]) return LANGUAGE_LABELS[code];
  try { return new Intl.DisplayNames(["en"], { type: "language" }).of(code) || code; } catch { return code; }
}
export function regionLabel(value: unknown): string {
  const code = text(value);
  if (!code) return UNSPECIFIED;
  if (code === "001") return "International";
  if (code === "419") return "Latin America";
  try { return new Intl.DisplayNames(["en"], { type: "region" }).of(code) || code; } catch { return code; }
}
const COUNTRY_ADJECTIVES: Record<string, string> = {
  "United States": "American", Australia: "Australian", Canada: "Canadian", "United Kingdom": "British",
  Ireland: "Irish", India: "Indian", "New Zealand": "New Zealand", "South Africa": "South African",
  Argentina: "Argentinian", Mexico: "Mexican", Spain: "Spanish", France: "French", Japan: "Japanese",
  "South Korea": "Korean", Indonesia: "Indonesian", Brazil: "Brazilian", Portugal: "Portuguese", Kenya: "Kenyan",
  Czechia: "Czech", "Czech Republic": "Czech", Germany: "German", Greece: "Greek", Italy: "Italian",
  Netherlands: "Dutch", Poland: "Polish", Vietnam: "Vietnamese", Egypt: "Egyptian",
};
export const countryGroupLabel = (value: unknown) => { const country = regionLabel(value); return COUNTRY_ADJECTIVES[country] || country; };
export function accentLabel(value: unknown, region?: unknown): string {
  const raw = text(value);
  const label = ACCENTS[raw] || raw || UNSPECIFIED;
  const country = regionLabel(region);
  // Keep distinct regions (e.g. Mexican under Latin America); strip only redundant prefixes.
  const parts = label.split(/\s+[–—-]\s+/);
  if (parts.length > 1 && [country, countryGroupLabel(region)].some(prefix => prefix.toLowerCase() === parts[0].toLowerCase())) return parts.slice(1).join(" – ");
  return label;
}
export function genderLabel(value: unknown): string {
  const raw = text(value).toLowerCase();
  if (["male", "masculine", "man"].includes(raw)) return "Male";
  if (["female", "feminine", "woman"].includes(raw)) return "Female";
  if (["neutral", "androgynous", "nonbinary", "non-binary"].includes(raw)) return "Neutral";
  return UNSPECIFIED;
}
export function pitchLabel(value: unknown): string {
  const raw = text(value).toLowerCase();
  return ["low","medium","high"].includes(raw) ? raw[0].toUpperCase()+raw.slice(1) : UNSPECIFIED;
}
